import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { post } from '../../../../util/request';
import { platform_base_url } from '../../../../util/config';

import * as Styled from './ForgotPasswordForm.styled';

const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Please enter a valid email address.')
    .required('Required')
});

const NOT_FOUND = 1001;

/**
 * Forgot Password Form allows a user to submit their email address to receive a password reset email
 */
const ForgotPasswordForm = ({ onComplete, onReject }) => {
  const [isFetching, setIsFetching] = useState(false);
  const handleSubmit = async ({ email }, { setErrors }) => {
    setIsFetching(true);

    await post(`${platform_base_url}/v2/identity/forgot-password`, {
      email
    })
      .then(() => onComplete(email))
      .catch((e) => {
        setIsFetching(false);
        const error = e?.response?.errors?.[0];

        // don't expose to the UI that a user isn't found
        if (error?.reference_code === NOT_FOUND) {
          onComplete(email);
        } else {
          setErrors({
            email: error?.title || 'Unable to contact server. Please try again.'
          });
        }
      });
  };

  return (
    <Styled.ForgotPasswordForm>
      <Styled.Heading>
        Enter your email address and we’ll send
        <Styled.Accent> a code to reset your password</Styled.Accent>. Codes are
        valid for 30 minutes for your security.
      </Styled.Heading>
      <Formik
        initialValues={{ email: '' }}
        validationSchema={ForgotPasswordSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Styled.TextInput
            id="forgotPasswordFormEmail"
            name="email"
            type="email"
            label="Email"
            autoComplete="email"
          />
          <Styled.Button
            isFull
            type="submit"
            size="large"
            variant="primary"
            isLoading={isFetching}
          >
            Send Me A Code
          </Styled.Button>
          <Styled.Button isFull size="medium" variant="link" onClick={onReject}>
            Cancel
          </Styled.Button>
        </Form>
      </Formik>
    </Styled.ForgotPasswordForm>
  );
};

ForgotPasswordForm.propTypes = {
  /**
   * A function that is called upon successful submission of the form.
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * A function that is called when requesting to leave the form.
   */
  onReject: PropTypes.func.isRequired
};

export default ForgotPasswordForm;
